import { useMemo, useState } from 'react'
import { Vector3 } from 'three'
import { useFrame } from '@react-three/fiber'
import Text3DThree, { Text3DThreeProps } from '../Text3DThree'

const cameraDirection = new Vector3()

export interface Text3DThreeDoubleSidedProps extends Text3DThreeProps {
  position: Vector3
  textXOrientation: Vector3
  textYOrientation: Vector3
  offsetBetweenTxtElementsHalfWidth?: number
}

const Text3DThreeDoubleSided = ({
  position,
  textXOrientation,
  textYOrientation,
  offsetBetweenTxtElementsHalfWidth = 0.005,
  disableDepthTest = false,

  ...props
}: Text3DThreeDoubleSidedProps) => {
  const textZDirection = useMemo(() => {
    return textXOrientation.clone().cross(textYOrientation).normalize()
  }, [textXOrientation, textYOrientation])

  // Without the depth test the label facing away from the camera would show through mirrored, so
  // only the one facing the camera is drawn (with it the depth test hides the other one).
  const [isFrontFacingCamera, setIsFrontFacingCamera] = useState(true)
  useFrame(({ camera }) => {
    if (!disableDepthTest) return
    const isFrontFacing = camera.getWorldDirection(cameraDirection).dot(textZDirection) <= 0
    setIsFrontFacingCamera(previous => (previous === isFrontFacing ? previous : isFrontFacing))
  })
  const showFront = !disableDepthTest || isFrontFacingCamera
  const showBack = !disableDepthTest || !isFrontFacingCamera
  // label 1
  const textXOrientation1 = textXOrientation.clone()
  const textYOrientation1 = textYOrientation.clone()

  // label 2
  const textXOrientation2 = textXOrientation.clone().multiplyScalar(-1)
  const textYOrientation2 = textYOrientation.clone()

  const position1 = useMemo(() => {
    return position
      .clone()
      .add(textZDirection.clone().multiplyScalar(offsetBetweenTxtElementsHalfWidth))
  }, [position, textZDirection, offsetBetweenTxtElementsHalfWidth])

  const position2 = useMemo(() => {
    return position
      .clone()
      .add(textZDirection.clone().multiplyScalar(-offsetBetweenTxtElementsHalfWidth))
  }, [position, textZDirection, offsetBetweenTxtElementsHalfWidth])

  const commonProps = {
    extrudeDepthSymmetrically: true,
    textDepth: 0.001,
    disableDepthTest,
  }

  return (
    <group>
      {showFront && (
        <Text3DThree
          {...props}
          position={position1}
          textXOrientation={textXOrientation1}
          textYOrientation={textYOrientation1}
          {...commonProps}
        />
      )}
      {showBack && (
        <Text3DThree
          {...props}
          position={position2}
          textXOrientation={textXOrientation2}
          textYOrientation={textYOrientation2}
          {...commonProps}
        />
      )}
    </group>
  )
}

export default Text3DThreeDoubleSided
